/* The judge page's save queue and card merging, as pure functions
 * (pages/judge.jsx drives them; utils/judge-queue.test.js checks them).
 *
 * Saves run one at a time. Each card's saves stay in order, but a card whose
 * save is waiting out a retry is stepped over, so one card's trouble never
 * holds up another's. What the server answers decides what happens next
 * (classifySave). When the server refuses a save, its copy of the card and
 * ours are merged (mergeCard): rounds the server has locked are the server's,
 * the judge's unsent scores on the other rounds are kept. */

const ROUNDS = 5;

// Refusals that retrying can't fix.
export const TERMINAL_SAVE_CODES = [
    "SESSION_ENDED",
    "EVENT_ENDED",
    "CARD_LOCKED",
    "NO_SUCH_MATCH",
    "ROUND_LOCKED",
    "ROUND_ORDER",
    "INVALID_CARD",
];

// An answer with no code, or one this page doesn't know, is tried this many
// times in all, then given up on. Network failures are retried until they
// get through: that is the venue's Wi-Fi dropping out.
export const MAX_SERVER_TRIES = 3;

// Backoff for a card's retries: 1 s, 2 s, 4 s … capped at 30 s.
export const RETRY_BASE_MS = 1000;
export const RETRY_MAX_MS = 30000;
export const retryDelay = (tries) => Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, tries - 1));

/**
 * Add a save to the queue (a new array). A draft replaces the card's last
 * queued draft when nothing of that card comes after it and it isn't on its
 * way already; a commit (NEXT ROUND) always gets its own place.
 * @param {Array<{id, boutKey, rounds, commitRound, red}>} queue
 * @param {{id, boutKey, rounds, commitRound, red}} item  red: the red
 *   corner's fighterId the rounds were scored against (judgeSaveCard turns
 *   a save round if the corners were swapped since)
 * @param {*} [inFlightId] id of the save being sent right now
 */
export function enqueue(queue, item, inFlightId = null) {
    const out = queue.slice();
    if (!item.commitRound) {
        let i = out.length - 1;
        while (i >= 0 && out[i].boutKey !== item.boutKey) i -= 1;
        const last = out[i];
        if (last && !last.commitRound && last.id !== inFlightId) {
            out[i] = { ...last, rounds: item.rounds, red: item.red ?? null };
            return out;
        }
    }
    out.push(item);
    return out;
}

/**
 * The save to send next: each card's oldest one, in queue order, skipping
 * cards that are waiting out a retry.
 * @param {Array} queue
 * @param {Object<string, number>} waitUntil  boutKey → earliest retry time (ms)
 * @param {number} now
 */
export function pickNext(queue, waitUntil, now) {
    const seen = new Set();
    for (const item of queue) {
        if (seen.has(item.boutKey)) continue;
        seen.add(item.boutKey);
        if ((waitUntil[item.boutKey] ?? 0) <= now) return item;
    }
    return null;
}

/** How long until pickNext has something (ms), or null for an empty queue. */
export function nextWakeIn(queue, waitUntil, now) {
    if (!queue.length) return null;
    const heads = [...new Set(queue.map((q) => q.boutKey))];
    return Math.max(0, Math.min(...heads.map((b) => (waitUntil[b] ?? 0) - now)));
}

/**
 * What to do with one save's outcome.
 * @param {{res?: object, networkError?: boolean, tries: number}} p
 *   tries: attempts at this save so far, this one included
 * @returns {"ok"|"terminal"|"retry"}
 */
export function classifySave({ res, networkError = false, tries }) {
    if (networkError) return "retry";
    if (res?.success) return "ok";
    if (TERMINAL_SAVE_CODES.includes(res?.code)) return "terminal";
    return tries >= MAX_SERVER_TRIES ? "terminal" : "retry";
}

/**
 * The server's copy of a card with ours laid over it. Rounds the server has
 * locked are the server's. On the others our scores win when `keepLocal`
 * (the judge has changes the server hasn't seen), else the server's do.
 * Locks are the server's alone: whatever we assumed locked but the server
 * doesn't have is undone.
 */
export function mergeCard(local, server, { keepLocal = false } = {}) {
    const locked = Array.from({ length: ROUNDS }, (_, i) => !!server.lockedRounds?.[i]);
    const rounds = Array.from({ length: ROUNDS }, (_, i) => {
        const theirs = server.rounds?.[i] ?? null;
        if (locked[i] || !keepLocal) return theirs;
        return local?.rounds?.[i] ?? theirs;
    });
    return { ...(local || {}), ...server, rounds, lockedRounds: locked };
}

/**
 * Rounds the server had locked (`before`, its last confirmed copy) and now
 * has unlocked (`after`): an admin unlock. 1-based, in order.
 */
export function unlockedRounds(before, after) {
    if (!before || !after) return [];
    const out = [];
    for (let i = 0; i < ROUNDS; i += 1) {
        if (before.lockedRounds?.[i] && !after.lockedRounds?.[i]) out.push(i + 1);
    }
    return out;
}

/**
 * The locks the page shows for a card: the server's, plus the rounds whose
 * NEXT ROUND is still queued (they lock as soon as the save lands).
 */
export function withPendingLocks(lockedRounds, queue, boutKey) {
    const out = Array.from({ length: ROUNDS }, (_, i) => !!lockedRounds?.[i]);
    queue.forEach((q) => {
        if (q.boutKey === boutKey && q.commitRound) out[q.commitRound - 1] = true;
    });
    return out;
}

/* Unsent saves survive a reload: kept per card in localStorage and sent
 * again, in order, when the page comes back. */

/** The queue as stored: { boutKey: [{rounds, commitRound}] }. */
export function serializeQueue(queue) {
    const out = {};
    queue.forEach((q) => {
        if (!out[q.boutKey]) out[q.boutKey] = [];
        out[q.boutKey].push({ rounds: q.rounds, commitRound: q.commitRound ?? null, red: q.red ?? null });
    });
    return out;
}

/** Stored saves back as queue items (ids from `nextId`), each card in order. */
export function restoreQueue(stored, nextId) {
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return [];
    const items = [];
    Object.entries(stored).forEach(([boutKey, list]) => {
        if (!Array.isArray(list)) return;
        list.forEach((s) => {
            if (!s || !Array.isArray(s.rounds)) return;
            const commitRound = Number.isInteger(s.commitRound) && s.commitRound >= 1 && s.commitRound <= ROUNDS ? s.commitRound : null;
            // Stored before corners were tracked: no red, nothing checked.
            const red = typeof s.red === "string" && s.red ? s.red : null;
            items.push({ id: nextId(), boutKey, rounds: s.rounds.slice(0, ROUNDS), commitRound, red });
        });
    });
    return items;
}

/**
 * A card from before rounds locked: rounds scored but none locked. The judge
 * confirms each with NEXT ROUND; the page says so. True on the round to lock
 * next while a later round already has a score and nothing after it is
 * locked (after an admin unlock, later rounds are locked, so it's not this).
 */
export function isLegacyScored(card, round) {
    const locked = card?.lockedRounds ?? [];
    const rounds = card?.rounds ?? [];
    if (locked[round - 1]) return false;
    if (locked.slice(round).some(Boolean)) return false;
    if (!rounds[round - 1]) return false;
    return rounds.slice(round).some(Boolean);
}
